import { type FileType } from "../models/constants/model-constants";
import { fileService } from "../services/file-service";
import { deleteFileFromDisk } from "./file-cleanup";
import { logger } from "./logger";

/**
 * Helper to create file info object from uploaded file (for inline storage in models)
 * This maintains backward compatibility with existing code
 */
export function createFileInfo(file: Express.Multer.File) {
  // Extract the subdirectory from destination (e.g., "uploads/profiles/" -> "profiles/")
  let subDir = file.destination || "";

  // Remove "uploads/" or "uploads\" prefix
  subDir = subDir.replace(/^uploads[\/\\]/, "");

  // Ensure subDir ends with / if it's not empty
  if (subDir && !subDir.endsWith("/") && !subDir.endsWith("\\")) {
    subDir += "/";
  }

  // Normalize path separators to forward slash
  subDir = subDir.replaceAll("\\", "/");

  // Get base URL from environment (should include protocol, domain, port, and /uploads)
  // Example: https://api.covalent.prodservers.com:3091/uploads
  const baseUrl = process.env.IMAGE_URL || "http://localhost:3080/uploads";

  // Construct full URL: baseUrl + subdirectory + filename
  const fileUrl = `${baseUrl}/${subDir}${file.filename}`;

  return {
    fileName: file.filename,
    originalName: file.originalname,
    fileSize: file.size,
    mimeType: file.mimetype,
    fileUrl,
    uploadedAt: new Date(),
  };
}

/**
 * Helper to process uploaded files and create file info objects (for inline storage)
 * This maintains backward compatibility with existing code
 */
export function processUploadedFiles(files: any): any {
  if (!files) return {};

  const result: any = {};

  // Process single files
  if (files.profileImage?.[0]) {
    result.profilePicture = createFileInfo(files.profileImage[0]);
  }

  if (files.logo?.[0]) {
    result.logo = createFileInfo(files.logo[0]);
  }

  // Process multiple files (documents)
  if (files.documents && Array.isArray(files.documents)) {
    result.documents = files.documents.map((file: Express.Multer.File) =>
      createFileInfo(file),
    );
  }

  return result;
}

/**
 * Helper to create Media records from uploaded files (recommended for new code)
 * This uses the Media model for better file tracking
 */
export async function processUploadedFilesWithMedia(
  files: any,
  userId: string,
  options?: {
    isPublic?: boolean;
    isTemporary?: boolean;
  },
): Promise<{
  profileImageId?: string;
  logoId?: string;
  documentIds?: string[];
}> {
  const result: any = {};

  try {
    // Process profile image
    if (files.profileImage?.[0]) {
      const media = await fileService.createMediaRecord(
        files.profileImage[0],
        userId,
        "profile_picture",
        options,
      );
      result.profileImageId = media.mediaId;
    }

    // Process logo
    if (files.logo?.[0]) {
      const media = await fileService.createMediaRecord(
        files.logo[0],
        userId,
        "school_logo",
        options,
      );
      result.logoId = media.mediaId;
    }

    // Process documents
    if (files.documents && Array.isArray(files.documents)) {
      result.documentIds = [];
      for (const file of files.documents) {
        const media = await fileService.createMediaRecord(
          file,
          userId,
          "document",
          options,
        );
        result.documentIds.push(media.mediaId);
      }
    }

    return result;
  } catch (error) {
    logger.error("Error processing uploaded files with Media:", error);
    throw error;
  }
}

/**
 * Helper to delete files when updating (e.g., replacing profile image)
 */
export async function deleteOldFile(
  fileName: string | undefined,
): Promise<void> {
  if (!fileName) return;

  try {
    await deleteFileFromDisk(fileName);
  } catch (error) {
    logger.error(`Error deleting old file ${fileName}:`, error);
  }
}

/**
 * Helper to delete multiple files
 */
export async function deleteOldFiles(fileNames: string[]): Promise<void> {
  for (const fileName of fileNames) {
    await deleteOldFile(fileName);
  }
}
